import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Edit3, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { appId, db } from '@/lib/firebase';
import {
  archiveStripeProduct,
  createStripeProduct,
  syncStripeProducts,
  updateStripeProduct,
  type BillingType,
  type ProductGroup,
  type StripePriceFormInput,
} from '@/lib/stripe-billing';

interface AdminStripeProductsProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

interface ProductPrice {
  id: string;
  unitAmount: number;
  currency: string;
  billingType: BillingType;
}

interface StripeProductDoc {
  stripeId: string;
  name: string;
  description: string;
  active: boolean;
  group: ProductGroup;
  visible: boolean;
  displayOrder: number;
  prices: ProductPrice[];
}

interface ProductFormState {
  name: string;
  description: string;
  group: ProductGroup;
  visible: boolean;
  displayOrder: number;
  unitAmount: number;
  currency: string;
  billingType: BillingType;
}

const GROUP_LABELS: Record<ProductGroup, string> = {
  va_services: 'Virtual Assistant Services',
  saas_subscriptions: 'SaaS Subscriptions',
  one_time_services: 'On-Demand Services',
};

const DEFAULT_FORM: ProductFormState = {
  name: '',
  description: '',
  group: 'one_time_services',
  visible: true,
  displayOrder: 100,
  unitAmount: 10000,
  currency: 'usd',
  billingType: 'one_time',
};

function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

export function AdminStripeProducts({ showNotify }: AdminStripeProductsProps): JSX.Element {
  const [products, setProducts] = useState<StripeProductDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<StripeProductDoc | null>(null);
  const [form, setForm] = useState<ProductFormState>(DEFAULT_FORM);

  useEffect(() => {
    const productsQuery = query(
      collection(db, 'artifacts', appId, 'stripeProducts'),
      orderBy('displayOrder', 'asc')
    );
    const unsubscribe = onSnapshot(
      productsQuery,
      (snapshot) => {
        const mapped: StripeProductDoc[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as Partial<StripeProductDoc>;
          return {
            stripeId: docSnap.id,
            name: data.name ?? 'Untitled product',
            description: data.description ?? '',
            active: data.active ?? true,
            group: data.group ?? 'one_time_services',
            visible: data.visible ?? false,
            displayOrder: typeof data.displayOrder === 'number' ? data.displayOrder : 999,
            prices: Array.isArray(data.prices) ? (data.prices as ProductPrice[]) : [],
          };
        });
        setProducts(mapped);
        setLoading(false);
      },
      () => {
        setProducts([]);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const groupedProducts = useMemo(() => {
    return {
      va_services: products.filter((product) => product.group === 'va_services'),
      saas_subscriptions: products.filter((product) => product.group === 'saas_subscriptions'),
      one_time_services: products.filter((product) => product.group === 'one_time_services'),
    };
  }, [products]);

  const handleSync = async (): Promise<void> => {
    setSyncing(true);
    try {
      const result = await syncStripeProducts();
      showNotify?.(`Synced ${result.synced} products from Stripe.`, 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sync Stripe products.';
      showNotify?.(message, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const openCreate = (): void => {
    setEditingProduct(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  };

  const openEdit = (product: StripeProductDoc): void => {
    const firstPrice = product.prices[0];
    setEditingProduct(product);
    setForm({
      name: product.name,
      description: product.description,
      group: product.group,
      visible: product.visible,
      displayOrder: product.displayOrder,
      unitAmount: firstPrice?.unitAmount ?? 10000,
      currency: firstPrice?.currency ?? 'usd',
      billingType: firstPrice?.billingType ?? 'one_time',
    });
    setShowForm(true);
  };

  const buildPricePayload = (): StripePriceFormInput => ({
    unitAmount: Math.round(form.unitAmount),
    currency: form.currency,
    billingType: form.billingType,
  });

  const handleSave = async (): Promise<void> => {
    if (!form.name.trim()) {
      showNotify?.('Product name is required.', 'error');
      return;
    }
    if (form.unitAmount <= 0) {
      showNotify?.('Price amount must be greater than zero.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingProduct) {
        await updateStripeProduct({
          productId: editingProduct.stripeId,
          name: form.name.trim(),
          description: form.description.trim(),
          group: form.group,
          visible: form.visible,
          displayOrder: form.displayOrder,
          price: buildPricePayload(),
        });
        showNotify?.('Product updated successfully.', 'success');
      } else {
        await createStripeProduct({
          name: form.name.trim(),
          description: form.description.trim(),
          group: form.group,
          visible: form.visible,
          displayOrder: form.displayOrder,
          price: buildPricePayload(),
        });
        showNotify?.('Product created successfully.', 'success');
      }
      setShowForm(false);
      setEditingProduct(null);
      setForm(DEFAULT_FORM);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save Stripe product.';
      showNotify?.(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (productId: string): Promise<void> => {
    try {
      await archiveStripeProduct(productId);
      showNotify?.('Product archived in Stripe and hidden from catalog.', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to archive product.';
      showNotify?.(message, 'error');
    }
  };

  const updateProductMetadata = async (
    productId: string,
    patch: Partial<Pick<StripeProductDoc, 'group' | 'visible' | 'displayOrder'>>
  ): Promise<void> => {
    await updateDoc(doc(db, 'artifacts', appId, 'stripeProducts', productId), {
      ...patch,
      updatedAt: serverTimestamp(),
    });
  };

  const renderProductCard = (product: StripeProductDoc): JSX.Element => (
    <div key={product.stripeId} className="border border-gray-200 rounded-xl p-4 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{product.name}</h3>
          <p className="text-xs text-gray-600 mt-1">{product.description || 'No description provided.'}</p>
          <p className="text-xs text-gray-500 mt-2">Stripe ID: {product.stripeId}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openEdit(product)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <Edit3 size={14} />
            Edit
          </button>
          <button
            type="button"
            onClick={() => handleArchive(product.stripeId)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} />
            Archive
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <label className="space-y-1">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Visibility</span>
          <select
            value={product.visible ? 'visible' : 'hidden'}
            onChange={(event) =>
              updateProductMetadata(product.stripeId, { visible: event.target.value === 'visible' })
            }
            className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs"
          >
            <option value="visible">Visible to customers</option>
            <option value="hidden">Hidden</option>
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Group</span>
          <select
            value={product.group}
            onChange={(event) =>
              updateProductMetadata(product.stripeId, { group: event.target.value as ProductGroup })
            }
            className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs"
          >
            <option value="va_services">VA Services</option>
            <option value="saas_subscriptions">SaaS Subscriptions</option>
            <option value="one_time_services">On-Demand Services</option>
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Display Order</span>
          <input
            type="number"
            value={product.displayOrder}
            onChange={(event) =>
              updateProductMetadata(product.stripeId, { displayOrder: Number(event.target.value) })
            }
            className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs"
          />
        </label>

        <div className="space-y-1">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Status</span>
          <p
            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              product.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {product.active ? 'Active' : 'Archived'}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Prices</p>
        <div className="flex flex-wrap gap-2">
          {product.prices.length > 0 ? (
            product.prices.map((price) => (
              <span
                key={price.id}
                className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
              >
                {formatAmount(price.unitAmount, price.currency)} · {price.billingType.replace('_', ' ')}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-500">No prices synced yet.</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <section className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Stripe Products & Pricing</h1>
            <p className="text-sm text-gray-600 mt-1">
              Sync products from Stripe, assign groupings, and control what customers see.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing...' : 'Sync from Stripe'}
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus size={15} />
              New Product
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-600">
          Loading Stripe products...
        </div>
      ) : (
        <div className="space-y-5">
          {(Object.keys(GROUP_LABELS) as ProductGroup[]).map((groupKey) => (
            <div key={groupKey} className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
              <h2 className="text-base font-semibold text-gray-900">{GROUP_LABELS[groupKey]}</h2>
              {groupedProducts[groupKey].length === 0 ? (
                <p className="text-sm text-gray-500">No products in this group yet.</p>
              ) : (
                <div className="space-y-3">{groupedProducts[groupKey].map(renderProductCard)}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-xl border border-gray-200 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                {editingProduct ? 'Edit Stripe Product' : 'Create Stripe Product'}
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <div className="p-6 grid gap-4 md:grid-cols-2">
              <label className="md:col-span-2 space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Product Name</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>

              <label className="md:col-span-2 space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Description</span>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>

              <label className="space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Group</span>
                <select
                  value={form.group}
                  onChange={(event) => setForm((prev) => ({ ...prev, group: event.target.value as ProductGroup }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="va_services">VA Services</option>
                  <option value="saas_subscriptions">SaaS Subscriptions</option>
                  <option value="one_time_services">On-Demand Services</option>
                </select>
              </label>

              <label className="space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Billing Type</span>
                <select
                  value={form.billingType}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, billingType: event.target.value as BillingType }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="one_time">One-time</option>
                  <option value="monthly">Monthly</option>
                  <option value="6_month">Every 6 months</option>
                  <option value="yearly">Yearly</option>
                </select>
              </label>

              <label className="space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Amount (cents)</span>
                <input
                  type="number"
                  min={1}
                  value={form.unitAmount}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, unitAmount: Number(event.target.value) || 0 }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>

              <label className="space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Currency</span>
                <input
                  type="text"
                  value={form.currency}
                  onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>

              <label className="space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Display Order</span>
                <input
                  type="number"
                  value={form.displayOrder}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, displayOrder: Number(event.target.value) || 0 }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>

              <label className="space-y-1.5">
                <span className="text-sm font-medium text-gray-700">Visibility</span>
                <select
                  value={form.visible ? 'visible' : 'hidden'}
                  onChange={(event) => setForm((prev) => ({ ...prev, visible: event.target.value === 'visible' }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="visible">Visible</option>
                  <option value="hidden">Hidden</option>
                </select>
              </label>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {editingProduct ? <Save size={15} /> : <Plus size={15} />}
                {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
